// ============================================================================
// chatInvites.ts — ССЫЛКИ-ПРИГЛАШЕНИЯ ЧАТА (SSoT/SRP).
//
// Тонкие обёртки над MessengerService (createConversationInvite /
// resolveConversationInvite / redeemConversationInvite) + построение публичной
// ссылки {baseUrl}/join/{code} и копирование в буфер.
//
// Вся семантика (роли participant|guest|viewer, TTL expires_in_minutes,
// лимит max_uses, гость без записи в entities) — на СЕРВЕРЕ, клиент её не
// дублирует. Resolve — публичный предпросмотр меты (БЕЗ id чата), Redeem —
// погашение (участник добавляется в чат; гость получает гостевой доступ).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { copyToClipboard } from '@/lib/clipboard';
import { publicBaseUrl } from '@/lib/publicBaseUrl';

export type InviteRole = 'participant' | 'guest' | 'viewer';

export const INVITE_ROLE_LABELS: Record<InviteRole, string> = {
  participant: 'Участник',
  guest: 'Гость',
  viewer: 'Только просмотр',
};

export interface InviteLinkOptions {
  roleOnJoin: InviteRole;
  /** Срок жизни ссылки в минутах; <=0 — без срока. */
  expiresInMinutes: number;
  /** Число погашений; 0 — без ограничения. */
  maxUses: number;
}

export interface InviteLink {
  code: string;
  url: string;
  expiresAt: string;
  maxUses: number;
  roleOnJoin: string;
}

export interface InviteMeta {
  title: string;
  kind: string;
  requiresAuth: boolean;
  roleOnJoin: string;
}

export interface RedeemResult {
  conversationId: string;
  role: string;
  guest: boolean;
  uses: number;
  maxUses: number;
}

/**
 * Внешний базовый URL для публичных ссылок (тот же источник, что у QR-входа):
 * 1) `base_url` из настроек активной базы (внешний адрес публикации);
 * 2) внешний URL платформы (`getExternalBaseUrl` — конфиг Go на desktop,
 *    а НЕ wails.localhost) / origin клиента.
 * Возвращает URL без хвостового слэша.
 */
export async function inviteBaseUrl(): Promise<string> {
  return publicBaseUrl();
}

/** Публичная ссылка-приглашение: {baseUrl}/join/{code}. */
export function buildInviteLink(baseUrl: string, code: string): string {
  return `${String(baseUrl || '').replace(/\/+$/, '')}/join/${code}`;
}

/** Создать ссылку-приглашение для чата (участник группы). */
export async function createConversationInvite(conversationId: string, opts: InviteLinkOptions): Promise<InviteLink> {
  const r: any = await getSDK().messenger.createConversationInvite({
    conversationId,
    maxUses: opts.maxUses,
    expiresInMinutes: opts.expiresInMinutes,
    roleOnJoin: opts.roleOnJoin,
  } as any);
  const code = String(r?.code || '');
  if (!code) throw new Error('Сервер не вернул код приглашения');
  const base = await inviteBaseUrl();
  return {
    code,
    url: buildInviteLink(base, code),
    expiresAt: String(r?.expiresAt || ''),
    maxUses: Number(r?.maxUses || 0),
    roleOnJoin: String(r?.roleOnJoin || opts.roleOnJoin),
  };
}

/** Публичный предпросмотр ссылки: мета (title/kind/requires_auth/role). */
export async function resolveConversationInvite(code: string): Promise<InviteMeta> {
  const r: any = await getSDK().messenger.resolveConversationInvite({ code } as any);
  return {
    title: String(r?.title || ''),
    kind: String(r?.kind || ''),
    requiresAuth: !!r?.requiresAuth,
    roleOnJoin: String(r?.roleOnJoin || ''),
  };
}

/** Погасить ссылку: авторизованный добавляется в чат, гость — гостевым доступом. */
export async function redeemConversationInvite(code: string): Promise<RedeemResult> {
  const r: any = await getSDK().messenger.redeemConversationInvite({ code } as any);
  const conversationId = String(r?.conversationId || '');
  if (!conversationId) throw new Error('Сервер не вернул чат приглашения');
  return {
    conversationId,
    role: String(r?.role || ''),
    guest: !!r?.guest,
    uses: Number(r?.uses || 0),
    maxUses: Number(r?.maxUses || 0),
  };
}

/** Скопировать ссылку в буфер (единый хелпер — WebView2/PWA-совместимый). */
export async function copyInviteLink(url: string): Promise<void> {
  await copyToClipboard(url);
}
